/**
 * Treppan Academy - Certificate View
 * Premium certificate display with QR code verification and download option
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Award, Download, Share2, ChevronLeft, Trophy,
  GraduationCap, Calendar, Hash, QrCode, CheckCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { QRCodeSVG } from 'qrcode.react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const colors = {
  background: '#0A0D14',
  surface: '#131825',
  gold: '#D4AF37',
  goldLight: '#F5E6B3',
  success: '#10B981',
  fail: '#EF4444',
  redSeal: '#B91C1C',
  textHeading: '#FFFFFF',
  textBody: '#94A3B8',
  textMuted: '#64748B',
  border: 'rgba(255, 255, 255, 0.08)'
};

// Certificate Component for rendering
const CertificateTemplate = ({ certificate, isDistinction }) => {
  const sealColor = isDistinction ? colors.gold : colors.redSeal;
  const sealText = isDistinction ? 'CERTIFIED EXCELLENCE' : 'CERTIFIED';
  
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div 
      id="certificate-template"
      className="relative w-[1056px] h-[816px] p-12"
      style={{ 
        backgroundColor: '#FEFEFE',
        backgroundImage: `
          linear-gradient(135deg, ${isDistinction ? 'rgba(212,175,55,0.03)' : 'rgba(185,28,28,0.02)'} 0%, transparent 50%),
          linear-gradient(225deg, ${isDistinction ? 'rgba(212,175,55,0.03)' : 'rgba(185,28,28,0.02)'} 0%, transparent 50%)
        `
      }}
    >
      {/* Decorative Border */}
      <div 
        className="absolute inset-4 border-2 rounded-lg"
        style={{ borderColor: isDistinction ? colors.gold : colors.redSeal, opacity: 0.3 }}
      />
      <div 
        className="absolute inset-6 border rounded-lg"
        style={{ borderColor: isDistinction ? colors.gold : colors.redSeal, opacity: 0.2 }}
      />
      
      {/* Watermark */}
      <div 
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ opacity: 0.03 }}
      >
        <GraduationCap style={{ width: 400, height: 400, color: '#000' }} />
      </div>

      {/* Content Container */}
      <div className="relative z-10 h-full flex flex-col items-center justify-between text-center">
        {/* Header */}
        <div className="pt-4">
          {/* Logo */}
          <div className="flex items-center justify-center gap-3 mb-4">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: isDistinction ? colors.gold : colors.redSeal }}
            >
              <GraduationCap className="w-7 h-7 text-white" />
            </div>
            <span 
              className="text-2xl font-bold tracking-wider"
              style={{ 
                color: isDistinction ? colors.gold : colors.redSeal,
                fontFamily: 'Outfit, sans-serif'
              }}
            >
              TREPPAN ACADEMY
            </span>
          </div>
          
          {/* Title */}
          <h1 
            className="text-4xl font-light tracking-[0.3em] uppercase"
            style={{ color: '#1F2937', fontFamily: 'Outfit, sans-serif' }}
          >
            Certificate
          </h1>
          <p 
            className="text-lg tracking-[0.2em] uppercase mt-1"
            style={{ color: '#6B7280' }}
          >
            of Completion
          </p>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center py-8">
          <p className="text-lg mb-2" style={{ color: '#6B7280' }}>
            This is to certify that
          </p>
          
          {/* Name */}
          <h2 
            className="text-5xl font-semibold mb-4 px-8"
            style={{ 
              color: '#111827',
              fontFamily: 'Outfit, sans-serif',
              borderBottom: `3px solid ${isDistinction ? colors.gold : colors.redSeal}`
            }}
          >
            {certificate.user_name}
          </h2>
          
          <p className="text-lg mb-6 max-w-xl" style={{ color: '#6B7280' }}>
            has successfully completed the
          </p>
          
          {/* Course */}
          <h3 
            className="text-3xl font-semibold mb-4"
            style={{ 
              color: isDistinction ? colors.gold : colors.redSeal,
              fontFamily: 'Outfit, sans-serif'
            }}
          >
            {certificate.course_title}
          </h3>
          
          <p className="text-lg mb-8" style={{ color: '#6B7280' }}>
            Certification
          </p>
          
          {/* Score and Grade */}
          <div className="flex items-center gap-8 mb-4">
            <div className="text-center">
              <p className="text-sm uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Score</p>
              <p 
                className="text-3xl font-bold"
                style={{ color: '#111827', fontFamily: 'Outfit, sans-serif' }}
              >
                {certificate.score}%
              </p>
            </div>
            <div 
              className="w-px h-12"
              style={{ backgroundColor: '#E5E7EB' }}
            />
            <div className="text-center">
              <p className="text-sm uppercase tracking-wider" style={{ color: '#9CA3AF' }}>Grade</p>
              <p 
                className="text-3xl font-bold capitalize"
                style={{ 
                  color: isDistinction ? colors.gold : colors.redSeal,
                  fontFamily: 'Outfit, sans-serif'
                }}
              >
                {certificate.grade}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="w-full flex items-end justify-between pb-4">
          {/* Seal */}
          <div className="relative">
            <div 
              className="w-28 h-28 rounded-full flex flex-col items-center justify-center"
              style={{ 
                backgroundColor: sealColor,
                boxShadow: `0 4px 12px ${sealColor}40`
              }}
            >
              <Award className="w-8 h-8 text-white mb-1" />
              <span 
                className="text-[8px] text-white text-center leading-tight font-semibold tracking-wider px-2"
              >
                {sealText}
              </span>
            </div>
          </div>
          
          {/* QR Code for Verification */}
          <div className="text-center">
            <QRCodeSVG 
              value={`${window.location.origin}/academy/verify/${certificate.certificate_number}`}
              size={80}
              level="M"
              fgColor="#111827"
              bgColor="transparent"
            />
            <p className="text-[9px] mt-1" style={{ color: '#9CA3AF' }}>
              Scan to verify
            </p>
          </div>
          
          {/* Date and ID */}
          <div className="text-center">
            <p className="text-sm" style={{ color: '#6B7280' }}>
              Issued on {formatDate(certificate.issued_at)}
            </p>
            <p className="text-xs mt-1" style={{ color: '#9CA3AF' }}>
              Certificate ID: {certificate.certificate_number}
            </p>
          </div>
          
          {/* Signature */}
          <div className="text-center">
            <div 
              className="w-40 border-b-2 mb-2"
              style={{ borderColor: '#D1D5DB' }}
            />
            <p className="text-sm font-semibold" style={{ color: '#111827' }}>
              Treppan Academy
            </p>
            <p className="text-xs" style={{ color: '#6B7280' }}>
              {certificate.organization}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function CertificateView() {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const certificateRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchCertificate();
  }, [certificateId]);

  const fetchCertificate = async () => {
    try {
      const res = await fetch(`${API_URL}/api/academy/certificates/${certificateId}?token=${token}`);
      const result = await res.json();
      
      if (result.success) {
        setCertificate(result.certificate);
      } else {
        toast.error('Certificate not found');
        navigate('/academy');
      }
    } catch (error) {
      console.error('Error fetching certificate:', error);
      toast.error('Failed to load certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const element = document.getElementById('certificate-template');
      if (!element) return;
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: '#FEFEFE'
      });
      
      const link = document.createElement('a');
      link.download = `${certificate.certificate_number}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success('Certificate downloaded!');
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
    } finally {
      setDownloading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${certificate.user_name}'s Certificate`,
          text: `I earned a ${certificate.grade} in ${certificate.course_title}!`,
          url: shareUrl
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareUrl);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="text-center">
          <Award className="h-12 w-12 mx-auto mb-4 animate-pulse" style={{ color: colors.gold }} />
          <p style={{ color: colors.textBody }}>Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (!certificate) return null;

  const isDistinction = certificate.grade === 'distinction';

  return (
    <div className="min-h-screen p-6 lg:p-8" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <Button 
          variant="ghost"
          onClick={() => navigate('/academy')}
          style={{ color: colors.textMuted }}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to Academy
        </Button>
        
        <div className="flex items-center gap-3">
          <Button 
            variant="outline"
            onClick={handleShare}
            style={{ borderColor: colors.border, color: colors.textBody }}
            data-testid="share-certificate-btn"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
          <Button 
            onClick={handleDownload}
            disabled={downloading}
            className="rounded-full font-semibold"
            style={{ backgroundColor: isDistinction ? colors.gold : colors.redSeal, color: '#fff' }}
            data-testid="download-certificate-btn"
          >
            <Download className="h-4 w-4 mr-2" />
            {downloading ? 'Downloading...' : 'Download'}
          </Button>
        </div>
      </div>

      {/* Certificate Info Bar */}
      <div 
        className="flex flex-wrap items-center justify-center gap-6 p-4 rounded-xl mb-8"
        style={{ backgroundColor: colors.surface, border: `1px solid ${colors.border}` }}
      >
        <div className="flex items-center gap-2">
          <Trophy className="h-5 w-5" style={{ color: isDistinction ? colors.gold : colors.redSeal }} />
          <span style={{ color: colors.textHeading }}>
            {isDistinction ? 'Distinction' : 'Pass'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5" style={{ color: colors.textMuted }} />
          <span style={{ color: colors.textBody }}>
            {new Date(certificate.issued_at).toLocaleDateString()}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Hash className="h-5 w-5" style={{ color: colors.textMuted }} />
          <span style={{ color: colors.textBody }}>
            {certificate.certificate_number}
          </span>
        </div>
      </div>

      {/* Certificate Preview */}
      <div 
        className="flex justify-center overflow-x-auto pb-8"
        style={{ 
          boxShadow: `0 20px 60px ${isDistinction ? colors.gold : colors.redSeal}15`
        }}
      >
        <div 
          className="rounded-xl overflow-hidden shadow-2xl"
          style={{ transform: 'scale(0.85)', transformOrigin: 'top center' }}
        >
          <CertificateTemplate 
            certificate={certificate} 
            isDistinction={isDistinction}
          />
        </div>
      </div>
    </div>
  );
}
